import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { CalendarIcon, NotificationBellIcon, UploadIcon, UserIcon } from '../../../../assets/svg';
import { PrimaryText, SecondaryText } from '../../../CustomList/CustomList.styles';
import {
  ActionMenusContainer,
  ActionMenuContainer,
  ActionMenuOptionContainer,
  IconAndTextContainer,
  FlexColumn,
  StyledIconForward,
} from '../UserAccountPanel.styles';
import { selectNotificationSettings, selectSaveStatus } from '../../../../redux/people/peopleSlice';
import { fetchNotificationSettings } from '../../../../redux/people/peopleThunks';
import { selectAllEvents } from '../../../../redux/availability/availabilitySlice';
import { fetchAvailabilityEvents } from '../../../../redux/availability/availabilityThunks';
import { selectHasPrivilegedRole } from '../../../../redux/slices/currentUserSlice';
import { LOADING_STATES } from '../../../../utils/constants/loadingStates';
import {
  GROUPED_NOTIFICATION_SETTINGS,
  convertToGroupedSettings,
} from '../../../../utils/constants/groupedNotifications';
import { handleChoosePhotos } from '../UserAccountPanel.handlers';

export const PERSONAL_DETAILS_OPTIONS_MENU = [
  {
    title: 'Upload new photo',
    icon: UploadIcon,
    condensed: true,

    handlerId: 'choosePhotos',
  },
  {
    title: 'Edit personal details',
    icon: UserIcon,
    condensed: true,

    handlerId: 'editDetails',
  },
];

export const ACCOUNT_CONFIGURATION_OPTIONS_MENU = [
  {
    icon: CalendarIcon,
    title: 'Availability',
    subtitle: (state) =>
      state.count ? `${state.count} unavailable event${state.count === 1 ? '' : 's'}` : 'No unavailable events',
    handlerId: 'availability',
  },
  {
    icon: NotificationBellIcon,
    title: 'Notifications',
    subtitle: ({ notifications: { count, enabled, status } }) =>
      status === LOADING_STATES.LOADING
        ? 'Saving notification settings...'
        : count
          ? `${enabled} of ${count} notification type${count === 1 ? '' : 's'} enabled`
          : 'No notification types enabled',
    disabled: ({ notifications: { status } }) => status === LOADING_STATES.LOADING,
    handlerId: 'editNotifications',
  },
];

const ActionMenus = ({ handleOpenDrawer, employee }) => {
  const dispatch = useDispatch();
  const status = useSelector(selectSaveStatus);
  const events = useSelector(selectAllEvents);
  const count = events.length;
  const notifications = useSelector(selectNotificationSettings);
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);

  useEffect(() => {
    if (status === LOADING_STATES.IDLE || status === LOADING_STATES.SUCCEEDED) {
      dispatch(fetchNotificationSettings(employee.id));
    }
  }, [status, dispatch, employee.id]);

  useEffect(() => {
    dispatch(fetchAvailabilityEvents(employee.id));
  }, [dispatch, employee.id]);

  // Map handlers to their IDs for easy lookup
  const handlers = {
    choosePhotos: () => handleChoosePhotos(employee, dispatch),
    editDetails: () => {
      handleOpenDrawer('editPersonalDetails', employee);
    },
    availability: () => {
      handleOpenDrawer('availability', employee);
    },
    editNotifications: () => {
      handleOpenDrawer('editNotifications', employee);
    },
  };

  // Grab relevant data to display on the menus
  const renderProp = (value) => {
    // Filter notification groups based on user role
    const visibleNotificationGroups = GROUPED_NOTIFICATION_SETTINGS.filter((group) => {
      // Show privileged groups only to privileged users
      if (group.isPrivileged) {
        return hasPrivilegedRole;
      }
      return true;
    });

    const totalGroups = visibleNotificationGroups.length;

    // Convert individual settings to grouped settings
    const groupedSettings = notifications ? convertToGroupedSettings(notifications) : {};

    // Count enabled groups
    const enabledGroups = visibleNotificationGroups.filter((group) => {
      return groupedSettings[group.id] !== false;
    }).length;

    const _notifications = {
      status,
      count: totalGroups,
      enabled: enabledGroups,
    };
    const _state = { count, notifications: _notifications };
    if (typeof value === 'function') {
      return value(_state);
    }
    return value;
  };

  return (
    <ActionMenusContainer>
      <ActionMenuContainer>
        {PERSONAL_DETAILS_OPTIONS_MENU.map((option, index, arr) => (
          <ActionMenuOptionContainer
            key={`photo-option-${index}`}
            condensed={option.condensed}
            isLast={index === arr.length - 1}
            onClick={() => handlers[option.handlerId]()}
          >
            <Typography variant="bodyRegular">{option.title}</Typography>
            <option.icon color="white" size="small" />
          </ActionMenuOptionContainer>
        ))}
      </ActionMenuContainer>

      <ActionMenuContainer>
        {ACCOUNT_CONFIGURATION_OPTIONS_MENU.map((option, index, arr) => (
          <ActionMenuOptionContainer
            key={`main-option-${index}`}
            isLast={index === arr.length - 1}
            disabled={renderProp(option.disabled)}
            onClick={() => handlers[option.handlerId]()}
          >
            <IconAndTextContainer>
              <option.icon />
              <FlexColumn>
                <PrimaryText variant="bodyRegular">{renderProp(option.title)}</PrimaryText>
                <SecondaryText variant="bodyRegular">{renderProp(option.subtitle)}</SecondaryText>
              </FlexColumn>
            </IconAndTextContainer>
            <StyledIconForward />
          </ActionMenuOptionContainer>
        ))}
      </ActionMenuContainer>
    </ActionMenusContainer>
  );
};

ActionMenus.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  handleOpenDrawer: PropTypes.func.isRequired,
};

export default ActionMenus;
